import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { SOURCES } from "@/pages/reports/accountNotes";

const MAX_ROWS = 500;
const num = (v) => Number(v || 0);
const money = (v) => num(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const drCr = (v) => `${money(Math.abs(num(v)))} ${num(v) < 0 ? "Cr" : num(v) > 0 ? "Dr" : ""}`.trim();

/** Right-hand drawer listing every journal line behind one statement amount, with the document each came from. */
export default function AccountLedgerDrawer({ target, companyId, onClose }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!target) return undefined;
    let live = true;
    setRows(null); setError("");
    const params = { company_id: companyId, account_id: target.accountId };
    if (target.dateFrom) params.date_from = target.dateFrom;
    if (target.dateTo) params.date_to = target.dateTo;
    api.get("/gl/ledger", { params })
      .then(({ data }) => { if (live) setRows(data); })
      .catch((err) => { if (live) setError(getErrorMessage(err)); });
    return () => { live = false; };
  }, [target, companyId]);

  const totalDr = (rows || []).reduce((s, r) => s + num(r.debit), 0);
  const totalCr = (rows || []).reduce((s, r) => s + num(r.credit), 0);
  const closing = rows && rows.length ? rows[rows.length - 1].running_balance : 0;
  const shown = rows && rows.length > MAX_ROWS ? rows.slice(-MAX_ROWS) : rows;

  return (
    <Sheet open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-3xl" data-testid="account-ledger-drawer">
        {target && (
          <>
            <SheetHeader>
              <SheetTitle><span className="font-mono text-sm text-muted-foreground">{target.code}</span> {target.name}</SheetTitle>
              <SheetDescription>
                {target.dateFrom ? `${fmtDate(target.dateFrom)} to ${fmtDate(target.dateTo)}` : `Everything up to ${fmtDate(target.dateTo)}`}
                {" "}— every entry that makes up this amount.
              </SheetDescription>
            </SheetHeader>
            <div className="mt-4">
              {error ? <p className="text-sm text-rose-500">{error}</p> : !rows ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading entries…</div>
              ) : rows.length === 0 ? (
                <p className="text-sm text-muted-foreground">No entries in this period.</p>
              ) : (
                <>
                  <div className="mb-3 grid grid-cols-3 gap-2 text-xs">
                    <div className="rounded-md border border-border p-2"><p className="text-muted-foreground">Total debit</p><p className="font-mono font-semibold">₹{money(totalDr)}</p></div>
                    <div className="rounded-md border border-border p-2"><p className="text-muted-foreground">Total credit</p><p className="font-mono font-semibold">₹{money(totalCr)}</p></div>
                    <div className="rounded-md border border-border p-2"><p className="text-muted-foreground">Balance</p><p className="font-mono font-semibold" data-testid="ledger-drawer-closing">₹{drCr(closing)}</p></div>
                  </div>
                  {rows.length > MAX_ROWS && <p className="mb-2 text-xs text-muted-foreground">Showing the latest {MAX_ROWS} of {rows.length} entries; balances include all of them.</p>}
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Date</TableHead><TableHead>From</TableHead><TableHead>Details</TableHead>
                      <TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead><TableHead className="text-right">Balance</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {shown.map((r, i) => {
                        const src = SOURCES[r.source_type] || { label: r.source_type || "Entry", to: null };
                        return (
                          <TableRow key={`${r.journal_entry_id}-${i}`}>
                            <TableCell className="whitespace-nowrap text-xs">{fmtDate(r.entry_date)}</TableCell>
                            <TableCell className="text-xs">
                              {src.to ? (
                                <Link to={src.to} className="inline-flex items-center gap-1 text-primary hover:underline" onClick={onClose}>{src.label}<ExternalLink className="h-3 w-3" /></Link>
                              ) : src.label}
                              {r.reference && <div className="font-mono text-[10px] text-muted-foreground">{r.reference}</div>}
                            </TableCell>
                            <TableCell className="max-w-[16rem] text-xs text-muted-foreground">{r.narration || "—"}</TableCell>
                            <TableCell className="text-right font-mono text-xs">{num(r.debit) ? money(r.debit) : ""}</TableCell>
                            <TableCell className="text-right font-mono text-xs">{num(r.credit) ? money(r.credit) : ""}</TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono text-xs">{drCr(r.running_balance)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
